import { Controller } from "@hotwired/stimulus"

// Gates sign-up behind an affirmative acceptance of the Terms and Privacy Policy.
//
// Two acceptance checkboxes — one under the social buttons, one in the email
// form — are two views of one decision, so checking either checks both.
//
// The social buttons keep their `disabled` attribute, which is what protects the
// no-JS path: without it, a member could reach OmniAuth without accepting, and
// Member.from_omniauth sets terms_accepted_out_of_band so the model would not
// stop them. But a disabled button dispatches no events, so a click on one would
// simply do nothing and leave the member guessing. applyGate makes disabled
// buttons transparent to pointer events instead, letting the click reach the
// wrapper, where #attempt explains what is missing.
export default class extends Controller {
  static targets = ["checkbox", "gatedControl", "warning"]

  connect() {
    this.sync()
  }

  // Turbo replaces the email form when validation fails, bringing a new
  // checkbox with it. Adopt whatever the current decision is.
  checkboxTargetConnected(checkbox) {
    checkbox.checked = this.accepted
    this.applyGate()
  }

  sync(event) {
    const source = event ? event.target : null
    const accepted = source ? source.checked : this.accepted

    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox !== source) checkbox.checked = accepted
    })

    this.applyGate(accepted)
    if (accepted) this.clearWarning()
  }

  // Bound to the wrapper around the social buttons and to the email form's
  // submit. Does nothing once the member has accepted.
  attempt(event) {
    if (this.accepted) return

    event.preventDefault()
    event.stopPropagation()
    this.warn()
  }

  applyGate(accepted = this.accepted) {
    this.gatedControlTargets.forEach((control) => {
      control.disabled = !accepted
      control.setAttribute("aria-disabled", String(!accepted))
      control.style.pointerEvents = accepted ? "" : "none"

      if (accepted) {
        control.removeAttribute("title")
      } else {
        control.setAttribute("title", "Confirm you are 13 or older and accept the Terms to continue")
      }
    })
  }

  warn() {
    this.warningTargets.forEach((warning) => warning.classList.remove("d-none"))
    this.checkboxTargets.forEach((checkbox) => checkbox.classList.add("is-invalid"))

    const checkbox = this.visibleCheckbox
    if (checkbox) {
      checkbox.scrollIntoView({ block: "center", behavior: "smooth" })
      checkbox.focus({ preventScroll: true })
    }
  }

  clearWarning() {
    this.warningTargets.forEach((warning) => warning.classList.add("d-none"))
    this.checkboxTargets.forEach((checkbox) => checkbox.classList.remove("is-invalid"))
  }

  // The in-form checkbox is hidden while the email accordion is collapsed;
  // point the member at whichever one they can actually see.
  get visibleCheckbox() {
    return this.checkboxTargets.find((checkbox) => checkbox.offsetParent !== null)
      || this.checkboxTargets[0]
  }

  get accepted() {
    return this.checkboxTargets.some((checkbox) => checkbox.checked)
  }
}
